import { ChevronRightIcon, TicketsIcon } from "lucide-react";
import Link from "@/components/ui/link";
import { cn } from "@/lib/utils";

/**
 * A pack pays for this — said wherever somebody is deciding to book.
 *
 * Two audiences, one component, because they are looking at the same row and
 * the wrong message to either is the failure:
 *
 * - **Somebody who holds a pack** needs to know it applies *before* they reach
 *   a payment screen. Without this they either pay cash for a class they had
 *   already bought, or they stall halfway through wondering.
 * - **Somebody who holds none** never learns packs exist, because the only
 *   place that said so was a page they had no reason to open. This is the
 *   discovery: the moment they are looking at a price is the moment "ten of
 *   these costs 46% less" is worth reading.
 *
 * Deliberately does not quote a specific pack to a non-holder when several
 * could apply — the percentage is the hook and the packs page is the answer.
 */
export function PackEligibilityNote({
  hiiveSlug,
  holding,
  percent,
  className,
}: {
  hiiveSlug: string;
  /**
   * The viewer's own pack that covers this, when they have one.
   *
   * Present means "you already own this" and the note stops selling; absent
   * means "you could" and it starts.
   *
   * `via` decides which of three true-but-different things it says. The one
   * that matters is `booked`: an enrolled batch pass reaches the weekly class
   * it already paid for and holds a seat in, and telling that member their pack
   * "covers this · 1 credit left" invites them to spend a credit they do not
   * have on a seat they already hold. They then meet a refusal at checkout with
   * no explanation of a booking nobody ever told them about.
   */
  holding?: {
    packName: string;
    creditsRemaining: number | null;
    via: "credit" | "included" | "booked";
  } | null;
  /**
   * What a pack saves across the things on this screen, as a range.
   *
   * A range rather than one number because a pack covering a ₹86,682 pass and a
   * ₹1,84,753 one saves 8% on the first and 57% on the second. Quoting the low
   * end alone sells the pack short; quoting the high end alone under an "up to"
   * is true but reads as a trick next to a row that plainly saves 8%.
   */
  percent?: { low: number; high: number } | null;
  className?: string;
}) {
  if (holding) {
    // A booked-out batch pass is the one holder state with somewhere left to
    // go: the member liked it enough to finish it, and the next cohort is on
    // sale. Without the link the note is a polite dead end — "you're booked,
    // and this costs money" — which reads as "you cannot have another", when
    // buying a second pass is the ordinary way to carry on.
    const shellClassName = cn(
      "flex items-center gap-2.5 rounded-2xl border border-success/25 bg-success/[0.07] px-3.5 py-2.5 dark:border-[#F5E6B8]/25 dark:bg-[#F5E6B8]/[0.06]",
      holding.via === "booked" &&
        "transition-colors hover:bg-success/[0.12] dark:hover:bg-[#F5E6B8]/[0.1]",
      className,
    );
    const body = (
      <>
        <TicketsIcon
          className="size-4 shrink-0 text-success dark:text-[#F5E6B8]"
          aria-hidden="true"
        />
        <p className="min-w-0 text-[13px] leading-snug text-text-secondary">
          {holding.via === "booked" ? (
            // Says two things, and needs both. "You're booked" is the answer to
            // why the page is showing a price at all. "Another night is a
            // separate booking" is what keeps it true on a week the member is
            // NOT booked into — the pass caps this class at one either way, so
            // it will not pay again regardless of which date they pick.
            <>
              Your{" "}
              <span className="font-semibold text-text-primary">
                {holding.packName}
              </span>{" "}
              already booked your seat in this class &mdash; check your bookings
              for the date.{" "}
              <span className="font-semibold text-text-primary">
                Join another batch
              </span>{" "}
              to come back for a second cycle.
            </>
          ) : (
            <>
              Your{" "}
              <span className="font-semibold text-text-primary">
                {holding.packName}
              </span>{" "}
              covers this
              {/* An entitlement costs nothing, so a credit count next to it is
                  noise at best and a contradiction at worst — the bonus is
                  still there on a pass with nothing left in it. */}
              {holding.via === "included"
                ? " · included, no credit used"
                : holding.creditsRemaining != null
                  ? ` · ${holding.creditsRemaining} credit${
                      holding.creditsRemaining === 1 ? "" : "s"
                    } left`
                  : ""}
            </>
          )}
        </p>
      </>
    );

    return holding.via === "booked" ? (
      <Link href={`/hiives/${hiiveSlug}/packs`} className={shellClassName}>
        {body}
      </Link>
    ) : (
      <div className={shellClassName}>{body}</div>
    );
  }

  return (
    <Link
      href={`/hiives/${hiiveSlug}/packs`}
      className={cn(
        "group flex items-center gap-2.5 rounded-2xl border border-purple-400/25 bg-purple-400/[0.07] px-3.5 py-2.5 transition-colors hover:bg-purple-400/[0.12]",
        className,
      )}
    >
      <TicketsIcon
        className="size-4 shrink-0 text-purple-600 dark:text-purple-300"
        aria-hidden="true"
      />
      <p className="min-w-0 flex-1 text-[13px] leading-snug text-text-secondary">
        {percent ? (
          <>
            Book this on a pack and{" "}
            <span className="font-semibold text-success dark:text-[#F5E6B8]">
              {percent.low === percent.high
                ? `save ${percent.low}%`
                : `save ${percent.low}–${percent.high}%`}
            </span>
          </>
        ) : (
          <>
            A pack covers this —{" "}
            <span className="font-semibold text-text-primary">see packs</span>
          </>
        )}
      </p>
      <ChevronRightIcon
        className="size-4 shrink-0 text-text-tertiary transition-transform group-hover:translate-x-0.5"
        aria-hidden="true"
      />
    </Link>
  );
}

/**
 * The smallest possible "a pack reaches this", for a row in a list of tickets.
 *
 * A whole note per ticket row would drown the prices it sits beside, but with
 * nothing at all a member choosing between four passes cannot see that two of
 * them are on their pack and two are not. One pill, no number.
 */
export function PackCoveredBadge({
  covered,
  className,
}: {
  /** True when the *viewer's own* pack reaches it, which earns the stronger colour. */
  covered: "owned" | "available";
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[9.5px] font-semibold tracking-wide uppercase",
        covered === "owned"
          ? "bg-success/15 text-success dark:bg-[#F5E6B8]/15 dark:text-[#F5E6B8]"
          : "bg-purple-400/15 text-purple-600 dark:text-purple-300",
        className,
      )}
    >
      <TicketsIcon className="size-2.5" aria-hidden="true" />
      {covered === "owned" ? "On your pack" : "Pack"}
    </span>
  );
}
